// in src/App.js
import React from 'react';
import {Admin, Resource} from 'admin-on-rest';
import Login from './Login';
import authClient from './authClient';
import FuseLayout from './Fuse/admin/FuseLayout';
import {AppTitle} from './Fuse/global/title';
import {MainMenu} from './Fuse/global/menu';
import {FuseFooter} from './Fuse/global/footer';
import {
    fuseRestClient,
    fuseDashboard
} from './Fuse';
import NotFound from './Fuse/notFound';
import fuseReducer from './Fuse/admin/fuseReducer';
import fuseSaga from './Fuse/admin/fuseSaga';
import customRoutes from './routes';
import {resolveAccess} from './Fuse/shared/authFunctions';

/* Imports: Locations :::::::::: */
import {
    fuseLocationList,
    fuseLocationShow,
    fuseLocationCreate,
    fuseLocationEdit,
    fuseLocationListViewOnly
} from './Fuse/Location/location'
import {
    fuseLocationDbaList,
    fuseLocationDbaShow,
    fuseLocationDbaCreate,
    fuseLocationDbaEdit,
    fuseLocationDbaListViewOnly
} from './Fuse/Location/dba';
import {
    fuseLocationCompanyList,
    fuseLocationCompanyShow,
    fuseLocationCompanyCreate,
    fuseLocationCompanyEdit,
    fuseLocationCompanyListViewOnly
} from './Fuse/Location/company';
import {
    fuseLocationTypeList,
    fuseLocationTypeCreate,
    fuseLocationTypeEdit,
    fuseLocationTypeListViewOnly
} from './Fuse/Location/type';
import {
    fuseLocationDistrictList,
    fuseLocationDistrictCreate,
    fuseLocationDistrictEdit,
    fuseLocationDistrictListViewOnly
} from './Fuse/Location/district';
import {
    fusePriceLevelList,
    fusePriceLevelCreate,
    fusePriceLevelListViewOnly
} from './Fuse/Location/priceLevel';
import {
    fuseLocationAdditionalTaxList,
    fuseLocationAdditionalTaxShow,
    fuseLocationAdditionalTaxCreate,
    fuseLocationAdditionalTaxEdit,
    fuseLocationAdditionalTaxListViewOnly
} from './Fuse/Location/additionalTax';

/* Imports: Products :::::::::: */
import {
    fuseProductList,
    fuseProductShow,
    fuseProductCreate,
    fuseProductEdit,
    fuseProductListViewOnly
} from './Fuse/Product/product';
import {
    fuseProductTypeList,
    fuseProductTypeCreate,
    fuseProductTypeEdit,
    fuseProductTypeListViewOnly
} from './Fuse/Product/productType';
import {
    fuseProductCategoryList,
    fuseProductCategoryListViewOnly,
    fuseProductCategoryCreate,
    fuseProductCategoryEdit
} from './Fuse/Product/category';
import {
    fuseProductAliasList,
    fuseProductAliasCreate,
    fuseProductAliasListViewOnly
} from './Fuse/Product/alias';
import {fuseDiscountList, fuseDiscountCreate, fuseDiscountListViewOnly} from "./Fuse/Product/discount";

/* Imports: Inventory :::::::::: */
import {
    fuseLocationInventoryList,
    fuseLocationInventoryListViewOnly,
    fuseLocationInventoryEdit,
    fuseLocationInventoryZeroOut
} from './Fuse/Inventory/locationInventory';

import {
    fuseRepackRetailBoxEdit
} from './Fuse/Inventory/repackRetailBox';
import {
    fuseTransferCreate
} from './Fuse/Inventory/transfer';
import {
    fuseTransferAuditList,
    fuseTransferAuditShow
} from './Fuse/Inventory/transferAudit';
import {
    fuseLocationAuditList
} from './Fuse/Inventory/locationAudit';

/* Loyalty Program :::::::::: */
import {
    fuseVipCardConfigList,
    fuseVipCardConfigCreate,
    fuseVipCardConfigEdit
} from './Fuse/Loyalty/vipCardConfig';
import {
    fuseLoyaltyRankList,
    fuseLoyaltyRankCreate,
    fuseLoyaltyRankEdit,
} from './Fuse/Loyalty/rank';
import {
    fuseLoyaltyIncentiveTierList,
    fuseLoyaltyIncentiveTierCreate,
    fuseLoyaltyIncentiveTierEdit,
} from './Fuse/Loyalty/incentiveTier';
import {
    fuseLoyaltyAccountList,
    fuseLoyaltyAccountListViewOnly,
    fuseLoyaltyAccountShow,
    fuseLoyaltyAccountCreate,
    fuseLoyaltyAccountEdit
} from './Fuse/Loyalty/account';
import {
    fuseLoyaltyAccountTransactionList
} from './Fuse/Loyalty/transaction';
import {
    fuseLoyaltyAccountLogList
} from './Fuse/Loyalty/log';

/* Imports: POS Kits :::::::::: */
import {
    fusePosKitList,
    fusePosKitListViewOnly,
    fusePosKitShow,
    fusePosKitCreate,
    fusePosKitEdit
} from './Fuse/Kit/posKit';

/* Imports: POS Users :::::::::: */
import {
    fusePosUserTypeList
} from './Fuse/User/posUserType';
import {
    fusePosUserList,
    fusePosUserListViewOnly,
    fusePosUserShow,
    fusePosUserCreate,
    fusePosUserEdit,
} from './Fuse/User/posUser';

/* Imports: Configuration :::::::::: */
import {
    fuseConfigurationList,
    fuseConfigurationListViewOnly,
    fuseSystemConfigurationEdit
} from './Fuse/Setting/configuration';

import getMuiTheme from 'material-ui/styles/getMuiTheme';
import FuseTheme from './Fuse/global/theme';

const App = () => (
    <Admin title={<AppTitle/>}
           key="Admin"
           theme={getMuiTheme(FuseTheme)}
           appLayout={FuseLayout}
           dashboard={fuseDashboard}
           menu={MainMenu}
           restClient={fuseRestClient({})}
           loginPage={Login}
           customRoutes={customRoutes}
           customReducers={{fuseNotification: fuseReducer}}
           customSagas={[fuseSaga]}
           authClient={authClient}
           catchAll={NotFound}
           footer={FuseFooter}
    >
        {permissions => [

            // Locations Resources

            resolveAccess(permissions, ['locations'])
                ? <Resource name="Locations"
                            list={fuseLocationList} show={fuseLocationShow}
                            create={fuseLocationCreate} edit={fuseLocationEdit}/>
                : null,
            resolveAccess(permissions, [], ['locations_vo'])
                ? <Resource name="Locations"
                            list={fuseLocationListViewOnly} show={fuseLocationShow}/>
                : null,
            resolveAccess(permissions, ['locations'])
                ? <Resource name="LocationTypes"
                            list={fuseLocationTypeList}
                            create={fuseLocationTypeCreate} edit={fuseLocationTypeEdit}/>
                : null,
            resolveAccess(permissions, [], ['locations_vo'])
                ? <Resource name="LocationTypes"
                            list={fuseLocationTypeListViewOnly}/>
                : null,
            resolveAccess(permissions, ['locations'])
                ? <Resource name="DBAs"
                            list={fuseLocationDbaList} show={fuseLocationDbaShow}
                            create={fuseLocationDbaCreate} edit={fuseLocationDbaEdit}/>
                : null,
            resolveAccess(permissions, [], ['pos_kit', 'pos_kit_vo'])
                ? <Resource name="DBAs"
                            list={fuseLocationDbaList}/>
                : null,
            resolveAccess(permissions, [], ['locations_vo'])
                ? <Resource name="DBAs"
                            list={fuseLocationDbaListViewOnly} show={fuseLocationDbaShow}/>
                : null,
            resolveAccess(permissions, ['pos_users', 'pos_kit', 'pos_kit_vo'])
                ? <Resource name="Districts"
                            list={fuseLocationDistrictList}/>
                : null,
            resolveAccess(permissions, ['locations'])
                ? <Resource name="Districts"
                            list={fuseLocationDistrictList}
                            create={fuseLocationDistrictCreate} edit={fuseLocationDistrictEdit}/>
                : null,
            resolveAccess(permissions, [], ['locations_vo'])
                ? <Resource name="Districts"
                            list={fuseLocationDistrictListViewOnly}/>
                : null,
            resolveAccess(permissions, ['locations'])
                ? <Resource name="Companies"
                            list={fuseLocationCompanyList} show={fuseLocationCompanyShow}
                            create={fuseLocationCompanyCreate} edit={fuseLocationCompanyEdit}/>
                : null,
            resolveAccess(permissions, [], ['locations_vo'])
                ? <Resource name="Companies"
                            list={fuseLocationCompanyListViewOnly} show={fuseLocationCompanyShow}/>
                : null,
            resolveAccess(permissions, ['locations'])
                ? <Resource name="PriceLevels"
                            list={fusePriceLevelList}
                            create={fusePriceLevelCreate}/>
                : null,
            resolveAccess(permissions, [], ['locations_vo'])
                ? <Resource name="PriceLevels"
                            list={fusePriceLevelListViewOnly}/>
                : null,
            resolveAccess(permissions, ['locations'])
                ? <Resource name="AdditionalTaxes"
                            list={fuseLocationAdditionalTaxList} show={fuseLocationAdditionalTaxShow}
                            create={fuseLocationAdditionalTaxCreate} edit={fuseLocationAdditionalTaxEdit}/>
                : null,
            resolveAccess(permissions, [], ['locations_vo'])
                ? <Resource name="AdditionalTaxes"
                            list={fuseLocationAdditionalTaxListViewOnly} show={fuseLocationAdditionalTaxShow}/>
                : null,
            resolveAccess(permissions, ['locations', 'locations_vo', 'pos_kit']) ?
                <Resource name="LocationNames"/> : null,
            resolveAccess(permissions, ['locations', 'locations_vo', 'inventory', 'inventory_vo', 'pos_users', 'pos_users_vo', 'pos_kit', 'pos_kit_vo']) ?
                <Resource name="LocationsForInventory"/> : null,
            resolveAccess(permissions, ['locations', 'locations_vo']) ? <Resource name="LocationsForTransfer"/> : null,
            resolveAccess(permissions, ['locations', 'locations_vo']) ? <Resource name="LocationsForStatus"/> : null,

            // Products Resources

            resolveAccess(permissions, ['products_all', 'products_np'], [])
                ? <Resource name="Products"
                            list={fuseProductList} show={fuseProductShow}
                            create={fuseProductCreate} edit={fuseProductEdit}/>
                : null,
            resolveAccess(permissions, [], ['products_vo'])
                ? <Resource name="Products"
                            list={fuseProductListViewOnly} show={fuseProductShow}/>
                : null,
            resolveAccess(permissions, ['products_all', 'products_np', 'inventory', 'locations'], [])
                ? <Resource name="ProductTypes"
                            list={fuseProductTypeList}
                            create={fuseProductTypeCreate} edit={fuseProductTypeEdit}/>
                : null,
            resolveAccess(permissions, [], ['products_vo'])
                ? <Resource name="ProductTypes"
                            list={fuseProductTypeListViewOnly}/>
                : null,
            resolveAccess(permissions, ['products_all', 'products_np'], [])
                ? <Resource name="ProductCategories"
                            list={fuseProductCategoryList}
                            create={fuseProductCategoryCreate} edit={fuseProductCategoryEdit}/>
                : null,
            resolveAccess(permissions, [], ['products_vo'])
                ? <Resource name="ProductCategories"
                            list={fuseProductCategoryListViewOnly}/>
                : null,
            resolveAccess(permissions, ['products_all', 'products_np'], [])
                ? <Resource name="ProductAliases"
                            list={fuseProductAliasList}
                            create={fuseProductAliasCreate}/>
                : null,
            resolveAccess(permissions, [], ['products_vo'])
                ? <Resource name="ProductAliases"
                            list={fuseProductAliasListViewOnly}/>
                : null,
            resolveAccess(permissions, ['products_all', 'products_np'], [])
                ? <Resource name="Discounts"
                            list={fuseDiscountList}
                            create={fuseDiscountCreate}/>
                : null,
            resolveAccess(permissions, [], ['products_vo'])
                ? <Resource name="Discounts"
                            list={fuseDiscountListViewOnly}/>
                : null,
            resolveAccess(permissions, ['products_all', 'products_np', 'products_vo'], []) ?
                <Resource name="ProductCategoryNames"/> : null,
            resolveAccess(permissions, ['products_all', 'products_np', 'products_vo'], []) ?
                <Resource name="ProductNames"/> : null,
            resolveAccess(permissions, ['products_all', 'products_np', 'products_vo'], []) ?
                <Resource name="ProductUnits"/> : null,
            resolveAccess(permissions, ['products_all', 'products_np'], []) ?
                <Resource name="ProductUnitCreate"/> : null,
            resolveAccess(permissions, ['products_all', 'products_np', 'products_vo'], []) ?
                <Resource name="ProductUnitsByTypeUnitType"/> : null,
            resolveAccess(permissions, ['products_all', 'products_np', 'products_vo'], []) ?
                <Resource name="ProductUnitsForAlias"/> : null,

            // Inventory Resources

            resolveAccess(permissions, ['inventory'])
                ? <Resource name="LocationInventory"
                            list={fuseLocationInventoryList}
                            edit={fuseLocationInventoryEdit} remove={fuseLocationInventoryZeroOut}/>
                : null,
            resolveAccess(permissions, [], ['inventory_vo'])
                ? <Resource name="LocationInventory"
                            list={fuseLocationInventoryListViewOnly}/>
                : null,
            resolveAccess(permissions, ['inventory'])
                ? <Resource name="InventoryRepacksRetailBoxes"
                            edit={fuseRepackRetailBoxEdit}
                />
                : null,
            resolveAccess(permissions, ['inventory'])
                ? <Resource name="InventoryTransfers"
                            create={fuseTransferCreate}/>
                : null,
            resolveAccess(permissions, ['inventory', 'inventory_vo'])
                ? <Resource name="InventoryTransferAudits"
                            list={fuseTransferAuditList} show={fuseTransferAuditShow}/>
                : null,
            resolveAccess(permissions, ['inventory', 'inventory_vo'])
                ? <Resource name="LocationAudits"
                            list={fuseLocationAuditList}/>
                : null,
            resolveAccess(permissions, ['inventory', 'inventory_vo']) ? <Resource name="LocationAuditContents"/> : null,
            resolveAccess(permissions, ['inventory', 'inventory_vo']) ?
                <Resource name="LocationAuditCollection"/> : null,
            resolveAccess(permissions, ['inventory', 'inventory_vo']) ?
                <Resource name="InventoryTransferContents"/> : null,
            resolveAccess(permissions, ['inventory']) ? <Resource name="ChangeInventoryTransferLocation"/> : null,

            // Loyalty Program Resources

            resolveAccess(permissions, ['loyalty_all'])
                ? <Resource name="LoyaltyRanks"
                            list={fuseLoyaltyRankList}
                            create={fuseLoyaltyRankCreate} edit={fuseLoyaltyRankEdit}/>
                : null,
            resolveAccess(permissions, [], ['loyalty_csr', 'loyalty_vo'])
                ? <Resource name="LoyaltyRanks" list={fuseLoyaltyRankList}/>
                : null,
            resolveAccess(permissions, ['loyalty_all'])
                ? <Resource name="LoyaltyIncentiveTiers"
                            list={fuseLoyaltyIncentiveTierList}
                            create={fuseLoyaltyIncentiveTierCreate} edit={fuseLoyaltyIncentiveTierEdit}/>
                : null,
            resolveAccess(permissions, [], ['loyalty_csr', 'loyalty_vo'])
                ? <Resource name="LoyaltyIncentiveTiers" list={fuseLoyaltyIncentiveTierList}/>
                : null,
            resolveAccess(permissions, ['loyalty_all'])
                ? <Resource name="VipCardConfigurations"
                            list={fuseVipCardConfigList}
                            create={fuseVipCardConfigCreate} edit={fuseVipCardConfigEdit}/>
                : null,
            resolveAccess(permissions, [], ['loyalty_csr', 'loyalty_vo'])
                ? <Resource name="VipCardConfigurations" list={fuseVipCardConfigList}/>
                : null,
            resolveAccess(permissions, ['loyalty_all', 'loyalty_csr'])
                ? <Resource name="LoyaltyAccounts"
                            list={fuseLoyaltyAccountList} show={fuseLoyaltyAccountShow}
                            create={fuseLoyaltyAccountCreate} edit={fuseLoyaltyAccountEdit}/>
                : null,
            resolveAccess(permissions, [], ['loyalty_vo'])
                ? <Resource name="LoyaltyAccounts"
                            list={fuseLoyaltyAccountListViewOnly} show={fuseLoyaltyAccountShow}/>
                : null,
            resolveAccess(permissions, ['loyalty_all', 'loyalty_vo'])
                ? <Resource name="LoyaltyAccountTransactions"
                            list={fuseLoyaltyAccountTransactionList}/>
                : null,
            resolveAccess(permissions, ['loyalty_all', 'loyalty_csr', 'loyalty_vo'])
                ? <Resource name="LoyaltyAccountLogs"
                            list={fuseLoyaltyAccountLogList}/>
                : null,
            resolveAccess(permissions, ['loyalty_all', 'loyalty_csr'])
                ? <Resource name="LoyaltyAccountIdAvailable"/>
                : null,
            resolveAccess(permissions, ['loyalty_all', 'loyalty_csr', 'loyalty_vo'])
                ? <Resource name="CustomerLedgerEntries"/>
                : null,

            // POS Kits Resources

            resolveAccess(permissions, ['pos_kit'])
                ? <Resource name="POSKits"
                            list={fusePosKitList} show={fusePosKitShow}
                            create={fusePosKitCreate} edit={fusePosKitEdit}/>
                : null,
            resolveAccess(permissions, [], ['pos_kit_vo'])
                ? <Resource name="POSKits"
                            list={fusePosKitListViewOnly} show={fusePosKitShow}/>
                : null,

            // POS Users Resources

            resolveAccess(permissions, ['pos_users', 'pos_users_vo'])
                ? <Resource name="POSUserTypes"
                            list={fusePosUserTypeList}/>
                : null,
            resolveAccess(permissions, ['pos_users'])
                ? <Resource name="POSUsers"
                            list={fusePosUserList} show={fusePosUserShow}
                            create={fusePosUserCreate} edit={fusePosUserEdit}/>
                : null,
            resolveAccess(permissions, [], ['pos_users_vo'])
                ? <Resource name="POSUsers"
                            list={fusePosUserListViewOnly} show={fusePosUserShow}/>
                : null,

            // Configuration/Settings Resources

            resolveAccess(permissions, ['configuration'])
                ? <Resource name="Configurations"
                            list={fuseConfigurationList}
                            edit={fuseSystemConfigurationEdit}/>
                : null,
            resolveAccess(permissions, [], ['configuration_vo'])
                ? <Resource name="Configurations"
                            list={fuseConfigurationListViewOnly}/>
                : null,
        ]}
    </Admin>
);

export default App;



// WEBPACK FOOTER //
// ./src/App.js